"use client";

import { useEffect } from "react";

/**
 * webpack 청크 로딩 실패 시 자동 새로고침 핸들러
 *
 * 새 배포 후 구 청크가 CDN에서 사라지면 클라이언트 네비게이션 시
 * "Cannot read properties of undefined (reading 'call')" 에러가 발생한다.
 * 이 컴포넌트는 해당 에러를 감지하고 페이지를 새로고침하여 새 배포의 에셋을 로딩한다.
 *
 * @see https://jhpnd.sentry.io/issues/7226309904/ (JCG-GAMJA-PRODUCTION-2E)
 */
export default function ChunkErrorHandler() {
  useEffect(() => {
    function isChunkLoadError(error: unknown): boolean {
      if (!error) return false;

      // ChunkLoadError (webpack이 명시적으로 던지는 경우)
      if (error instanceof Error && error.name === "ChunkLoadError") {
        return true;
      }

      const message =
        error instanceof Error ? error.message : String(error);

      // Chrome: "Cannot read properties of undefined (reading 'call')"
      // Safari: "undefined is not an object (evaluating 'e[d].call')"
      if (message.includes("reading 'call'") || message.includes(".call'")) {
        return true;
      }

      // "Loading chunk X failed" (webpack dynamic import 실패)
      if (/loading chunk .+ failed/i.test(message)) {
        return true;
      }

      return false;
    }

    function reload() {
      const key = "chunk_error_reload";
      const last = sessionStorage.getItem(key);
      const now = Date.now();

      // 10초 내 중복 새로고침 방지 (무한 루프 차단)
      if (last && now - parseInt(last, 10) < 10_000) return;

      sessionStorage.setItem(key, now.toString());
      window.location.reload();
    }

    function handleRejection(e: PromiseRejectionEvent) {
      if (isChunkLoadError(e.reason)) {
        e.preventDefault();
        reload();
      }
    }

    function handleError(e: ErrorEvent) {
      if (isChunkLoadError(e.error)) {
        e.preventDefault();
        reload();
      }
    }

    window.addEventListener("unhandledrejection", handleRejection);
    window.addEventListener("error", handleError);

    return () => {
      window.removeEventListener("unhandledrejection", handleRejection);
      window.removeEventListener("error", handleError);
    };
  }, []);

  return null;
}
